import React from 'react'
import { FormLabel, Box, Switch, Tooltip, SwitchProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { TransactionTypeShort } from 'types/TransactionType'
import { IssueType, TransactionType } from '@finance-lab/utils/types'

export type EntityTypeShortType = (typeof TransactionTypeShort)[keyof typeof TransactionTypeShort]

const StyledSwitch = React.forwardRef<
    HTMLButtonElement,
    Omit<SwitchProps, 'color' | 'label'> & {
        color: EntityTypeShortType
        label: EntityTypeShortType
    }
>(function StyleSwitch({ color, ...props }, ref) {
    return <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple ref={ref} {...props} />
})

const IssueTypeSwitch = styled(StyledSwitch)(({ color, label, theme }) => {
    const width = 52
    const height = width * 0.48

    return {
        width,
        height,
        padding: 0,
        overflow: 'initial',
        '& .MuiSwitch-switchBase': {
            padding: 0,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: `translateX(${width * 0.4}px)`,
                color: color === 'TO' ? '#000' : '#fff',
                '& + .MuiSwitch-track': {
                    backgroundColor: theme.palette[color].main,
                },
                '& .MuiSwitch-thumb': {
                    backgroundColor: theme.palette[color].main,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                outline: '2px solid #000',
                textShadow: '0 0 3px #000',
            },
            '& .MuiSwitch-thumb::after': {
                content: `'${label}'`,
                textAlign: 'center',
                display: 'block',
                fontWeight: 'bold',
            },
            '& .MuiSwitch-thumb': {
                backgroundColor: theme.palette.grey[500],
                borderRadius: 8,
                padding: 2,
                boxSizing: 'border-box',
                width: width * 0.65,
                height,
                transition: theme.transitions.create(['opacity'], {
                    duration: 100,
                }),
            },
        },
        '& .MuiSwitch-track': {
            borderRadius: 8,
            backgroundColor: theme.palette.grey[400],
            opacity: 0.5,
            transition: theme.transitions.create(['opacity'], {
                duration: 100,
            }),
        },
    }
})

export type IssueTypeWarrantConvertibleSmall =
    | Exclude<TransactionType, 'Issue'>
    | Exclude<IssueType, 'Guarantor fee issue'>

type EntityTypeItem<TypeId extends IssueTypeWarrantConvertibleSmall = IssueTypeWarrantConvertibleSmall> = {
    id: TypeId
    label: string
    tooltipLabel: string
}

const types: Array<EntityTypeItem> = [
    { id: 'Rights issue', label: 'Företrädesemission', tooltipLabel: 'Visa företrädesemissioner' },
    { id: 'IPO', label: 'IPO', tooltipLabel: 'Visa IPO' },
    { id: 'Directed issue', label: 'Riktad emission', tooltipLabel: 'Visa riktade emissioner' },
    { id: 'Non-cash issue', label: 'Apportemission', tooltipLabel: 'Visa apportemissioner' },
    { id: 'Warrant', label: 'Teckningstoptioner', tooltipLabel: 'Visa teckningsoptioner' },
    { id: 'Convertible', label: 'Konvertibler', tooltipLabel: 'Visa konvertibler' },
]

function ToggleIssueTypes<
    ExcludedTypes extends IssueTypeWarrantConvertibleSmall,
    AvailableType extends IssueTypeWarrantConvertibleSmall = Exclude<IssueTypeWarrantConvertibleSmall, ExcludedTypes>,
>({
    dense = false,
    disabledItems = [],
    onChange,
    value,
    excludeTypes = [],
}: {
    dense?: boolean
    disabledItems?: IssueTypeWarrantConvertibleSmall[]
    onChange: (v: AvailableType[]) => void
    value: AvailableType[]
    excludeTypes?: ExcludedTypes[]
}) {
    const handleToggle = (typeId: AvailableType) => () => {
        let newState
        if (value.includes(typeId)) {
            newState = value.filter((v) => v !== typeId)
        } else {
            newState = [...value, typeId]
        }

        onChange(newState)
    }

    const isIncludedType = (type: EntityTypeItem): type is EntityTypeItem<AvailableType> =>
        !excludeTypes.some((t) => t === type.id)

    return (
        <Box
            component='fieldset'
            sx={{
                // border and margin is set in order to imitate MUI form fields
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                marginTop: '-9px',
                paddingTop: 0,
                paddingBottom: 0,
                height: `calc(100% + ${dense ? '-7px' : '9px'})`,
            }}
        >
            <Box component='legend'>
                <FormLabel sx={{ fontSize: '0.75em' }}>Emissionstyp</FormLabel>
            </Box>
            <Box
                sx={{
                    alignContent: 'space-around',
                    marginTop: '-10px',
                    paddingTop: 4,
                    paddingBottom: 4,
                    justifyContent: 'space-between',
                    display: 'flex',
                    gap: '26px 0',
                    flexFlow: 'wrap',
                    height: 'calc(100% + 10px)',
                }}
            >
                {types.filter(isIncludedType).map((issueType) => (
                    <Box
                        key={issueType.id}
                        sx={{ flexBasis: dense ? undefined : '50%', textAlign: 'center' }}
                        px={dense ? 2 : undefined}
                    >
                        <Tooltip title={issueType.tooltipLabel}>
                            <IssueTypeSwitch
                                key={issueType.id}
                                disabled={disabledItems.includes(issueType.id)}
                                onChange={handleToggle(issueType.id)}
                                checked={!disabledItems.includes(issueType.id) && value.includes(issueType.id)}
                                color={TransactionTypeShort[issueType.id]}
                                label={TransactionTypeShort[issueType.id]}
                                slotProps={{
                                    input: {
                                        'aria-label': issueType.label,
                                    },
                                }}
                            />
                        </Tooltip>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export default ToggleIssueTypes
